import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, ArrowUpRight } from "lucide-react";
import { NAV, STUDIO } from "../../data/site";

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled || open ? "bg-obsidian/90 backdrop-blur-xl border-b border-ash" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-[1700px] mx-auto px-5 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
            <span className="w-2 h-2 bg-violet group-hover:rotate-45 transition-transform duration-500 shrink-0" />
            <img src={STUDIO.logo} alt="Obsidian Art & Tattoo Studio" className="h-9 sm:h-10 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-mono text-[0.68rem] uppercase tracking-[0.18em] link-underline transition-colors ${
                    isActive ? "text-violet" : "text-[#C4C4CC] hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/booking"
              data-testid="nav-book-button"
              className="group inline-flex items-center gap-2 font-mono text-[0.68rem] uppercase tracking-[0.18em] font-bold bg-violet text-obsidian pl-6 pr-5 py-3 rounded-full hover:bg-white transition-colors duration-300"
            >
              Book <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-2 -mr-2 flex items-center gap-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.2em]">{open ? "Close" : "Menu"}</span>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <div
        data-testid="mobile-menu"
        className={`fixed inset-0 z-40 bg-obsidian transition-all duration-500 lg:hidden ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 violet-glow opacity-60" />
        <div className="relative h-full flex flex-col justify-center px-6 gap-1">
          {NAV.map((n, i) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="display text-6xl text-white py-3 border-b border-ash flex items-center justify-between hover:text-violet transition-colors"
            >
              {n.label}
              <span className="eyebrow">0{i + 1}</span>
            </Link>
          ))}
          <Link
            to="/booking"
            data-testid="mobile-nav-book"
            className="mt-8 bg-violet text-obsidian font-mono uppercase tracking-[0.2em] text-sm font-bold py-5 text-center rounded-full"
          >
            Book a Session
          </Link>
          <div className="mt-8 flex items-center gap-6 text-[#828290]">
            <a href={STUDIO.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest"><Instagram size={16} /> Instagram</a>
            <a href={STUDIO.phoneHref} className="font-mono text-xs uppercase tracking-widest">{STUDIO.phone}</a>
          </div>
        </div>
      </div>
    </>
  );
}
